import React, { forwardRef } from 'react';
import clsx from 'clsx';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual variant */
  variant?: 'default' | 'elevated' | 'bordered' | 'gradient-border';
  /** Inner padding size */
  padding?: 'sm' | 'md' | 'lg';
  /** Enable hover-lift interaction (translateY + shadow) */
  hover?: boolean;
}

const variantClasses: Record<NonNullable<CardProps['variant']>, string> = {
  default: 'bg-[var(--volt-dark-surface)]',
  elevated: 'bg-[var(--volt-dark-elevated)] shadow-[var(--volt-shadow-md)]',
  bordered: [
    'bg-[var(--volt-dark-surface)] border border-[var(--volt-dark-border)]',
    'transition-[border-color] duration-[var(--volt-transition-fast)]',
    'hover:border-[var(--volt-dark-border-hover)]',
  ].join(' '),
  'gradient-border': 'volt-gradient-border',
};

const paddingClasses: Record<NonNullable<CardProps['padding']>, string> = {
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      hover = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'rounded-[var(--volt-radius-lg)]',
          variantClasses[variant],
          paddingClasses[padding],
          hover && [
            'transition-all duration-[var(--volt-transition-fast)]',
            'hover:-translate-y-0.5 hover:shadow-[var(--volt-shadow-lg)]',
            'cursor-pointer',
          ],
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Card.displayName = 'Card';
